// ABOUTME: This component provides navigation prefetching functionality to improve page transition speed
// ABOUTME: It prefetches data when users hover over navigation links for instant page loads

import { useEffect } from "react";
import { useSelector } from "react-redux";
import { cemeterySlice } from "../services/cemeterySlice";
import { customerSlice } from "../services/customerSlice";
import { orderSlice } from "../services/orderSlice";
import { tenantSlice } from "../services/tenantSlice";
import { RootState } from "../store";
import { hasRole } from "../utils/roleHelpers";

export const NavigationPrefetch = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const currentTenantSlug = user?.tenantSlug;

  // prefetch hooks
  const prefetchOrders = orderSlice.usePrefetch("getAllOrders");
  const prefetchUserOrders = orderSlice.usePrefetch("getOrders");
  const prefetchCustomers = customerSlice.usePrefetch("getCustomers");
  const prefetchTenants = tenantSlice.usePrefetch("getAllTenants");
  const prefetchCemeteries = cemeterySlice.usePrefetch("getCemeteries");

  // prefetch tenant info for current tenant
  const prefetchTenantBySlug = tenantSlice.usePrefetch("getTenantBySlug");

  useEffect(() => {
    if (!user) return;

    // prefetch based on user role and current route
    const isAdmin = hasRole(user.roles, "tenant-owner");
    const isTenantManager =
      hasRole(user.roles, "tenant-manager") || hasRole(user.roles, "developer");

    // prefetch critical data based on role
    if (isAdmin) {
      // admin users likely to view orders and customers
      prefetchOrders(undefined, { force: false });
      prefetchCustomers(undefined, { force: false });
      prefetchCemeteries(undefined, { force: false });
    } else if (hasRole(user.roles, "customer")) {
      // customers only see their own orders
      prefetchUserOrders(undefined, { force: false });
      prefetchCemeteries(undefined, { force: false });
    }

    if (isTenantManager) {
      // tenant managers view tenant list
      prefetchTenants(undefined, { force: false });
    }

    // prefetch tenant branding for current tenant
    if (currentTenantSlug) {
      prefetchTenantBySlug(currentTenantSlug, { force: false });
    }
  }, [
    user,
    currentTenantSlug,
    prefetchOrders,
    prefetchUserOrders,
    prefetchCustomers,
    prefetchTenants,
    prefetchCemeteries,
    prefetchTenantBySlug,
  ]);

  return null; // this component doesn't render anything
};

export default NavigationPrefetch;
